<template>
  <oie-my-pages-template
    v-if="currentShoppingList">
    <oie-action-wrapper>
      <template #left>
        <oie-base-action theme="flat" @click="handleCancel">
          <template #iconPrepend>
            <oie-base-icon icon="arrow-left" size="s"></oie-base-icon>
          </template>
          {{ tStatic('goBack') }}
        </oie-base-action>
      </template>
      <template #right>
        <oie-message-bar
          v-if="hasGlobalSelection">
          {{ machineMessage }}
        </oie-message-bar>
      </template>
    </oie-action-wrapper>

    <!-- LIST INFO -->
    <oie-action-wrapper class="my-shopping-list__info">
      <template #left>
        <div>
          <oie-action-wrapper>
            <template #left>
              <h1 class="h2-semi-bold">
                {{ currentShoppingList.name }}
              </h1>
              <oie-add-option-slide-in
                :label-text="tStatic('editShoppingListNameLabel')"
                :title="tStatic('editShoppingListNameHeader')"
                :text="tStatic('editShoppingListNameText')"
                :initial-value="currentShoppingList.name"
                @add-option="(name) => {
                  updateCurrentShoppingListProperty({ property: 'name', value: name });
                  setActiveSlideIn(null);
                }">
                <template #activator="{open}">
                  <oie-base-action theme="flat" @click="open">
                    <template #iconPrepend>
                      <oie-base-icon icon="edit" size="s"></oie-base-icon>
                    </template>
                  </oie-base-action>
                </template>
              </oie-add-option-slide-in>
            </template>
          </oie-action-wrapper>

          <oie-action-wrapper>
            <template #left>
              <p class="body-italic">
                {{ currentShoppingList.description || tStatic('noDescription') }}
              </p>
              <oie-add-option-slide-in
                :label-text="tStatic('editShoppingListDescriptionLabel')"
                :title="tStatic('editShoppingListDescriptionHeader')"
                :text="tStatic('editShoppingListDescriptionText')"
                :initial-value="currentShoppingList.description"
                @add-option="(description) => {
                  updateCurrentShoppingListProperty({ property: 'description', value: description });
                  setActiveSlideIn(null);
                }">
                <template #activator="{open}">
                  <oie-base-action theme="flat" @click="open">
                    <template #iconPrepend>
                      <oie-base-icon icon="edit" size="s"></oie-base-icon>
                    </template>
                  </oie-base-action>
                </template>
              </oie-add-option-slide-in>
            </template>
          </oie-action-wrapper>

          <p class="caption-faint">
            {{ tStatic('createdList') }}: {{ currentShoppingList.created }}
          </p>

          <p class="caption-faint">
            {{ tStatic('modifiedList') }}: {{ currentShoppingList.modified }}
          </p>
        </div>
      </template>

      <div v-if="hasMyMachines" class="my-shopping-list__machine-selection">
        <div class="my-shopping-list__machine-selection-actions">
          <oie-my-machines-selector-slide-in
            @select-machine="handleMarkListWithMachineOrOwner"
            @select-owner="handleMarkListWithMachineOrOwner">
            <template #activator="{ open }">
              <oie-base-action
                v-tooltip.top="{content: t('markListTooltip')}"
                size="large"
                theme="outlined"
                @click="open">
                <template #iconPrepend>
                  <oie-base-icon icon="machine" size="s"></oie-base-icon>
                </template>
                {{ (currentShoppingList.machineId || currentShoppingList.machineOwnerId) ? tStatic('changeMachine') : t('markListButtonText') }}
              </oie-base-action>
            </template>
          </oie-my-machines-selector-slide-in>
          <oie-base-action
            v-if="currentShoppingList.machineId || currentShoppingList.machineOwnerId"
            v-tooltip.right="tStatic('clearMachineTooltip')"
            theme="destructive"
            size="large"
            @click="handleMarkListWithMachineOrOwner({})">
            <template #iconPrepend>
              <oie-base-icon icon="close" size="s"></oie-base-icon>
            </template>
          </oie-base-action>
        </div>
        <p v-if="currentShoppingList.machineOwnerId" class="body-italic my-shopping-list__machine-selection-info">
          <oie-base-icon icon="user-avatar" size="s"></oie-base-icon>{{ machineOwnerDictionary[currentShoppingList.machineOwnerId] }}
        </p>
        <p v-if="currentShoppingList.machineId" class="body-italic my-shopping-list__machine-selection-info">
          <oie-base-icon icon="machine" size="s"></oie-base-icon> {{ machineDictionary[currentShoppingList.machineId] }}
        </p>
      </div>
    </oie-action-wrapper>

    <!-- SEARCH -->
    <oie-article-search
      :name="currentShoppingList.id"></oie-article-search>

    <!-- PRODUCTS -->
    <oie-cart-products-table
      :rows="currentShoppingList.rows"
      :selected-rows="selectedRows"
      @update:selectedRows="setSelectedRows"
      @updateQuantity="({row, quantity}) => updateRow({ row, value: quantity, property: 'quantity' })"
      @updateComment="({row, comment, callback}) => {
        updateRow({ row, value: comment, property: 'comment' })
        callback.success()
      }"
      @updateAssemblyNote="({row, comment, callback}) => {
        updateRow({ row, value: comment, property: 'assemblyNote' })
        callback.success()
      }"
      @updatePrice="({row, price}) => updateRow({ row, value: price, property: 'price' })"
      @updateMachineOrOwner="handleUpdateMachineOrOwner"
      @updateMandatoryComment="({row, mandatoryComment}) => updateRow({ row, value: mandatoryComment, property: 'mandatoryComment' })"
      @deleteRow="({row}) => deleteRow(row)"
      @markSelectedRows="handleMarkSelectedRows"
      @deleteSelectedRows="deleteSelectedRows"
      @add-file-to-list="addRows($event)">
      <template #customMultiActions>
        <oie-base-action
          theme="outlined"
          :disabled="selectedRows.length === 0 ? true : false"
          @click="addSelectedRowsToCart">
          <template #iconPrepend>
            <oie-base-icon icon="shopping-cart-arrow-down" size="xs"></oie-base-icon>
          </template>
          {{ tStatic('addSelectedRowsToCart') }}
        </oie-base-action>
      </template>
    </oie-cart-products-table>

    <oie-action-wrapper>
      <oie-message-box class="my-shopping-list__summary">
        <dl class="body-bold">
          <dt>{{ tStatic('cartSummaryTotal') }}:</dt>
          <dd>{{ formatPrice(totalPriceCurrentShoppingList) }}</dd>
        </dl>
      </oie-message-box>
    </oie-action-wrapper>

    <!-- ACTIONS -->
    <oie-action-wrapper>
      <template #left>
        <oie-base-action
          v-element-loader="{ endpoint: 'shoppinglist' }"
          size="large"
          :disabled="!currentShoppingListHasChanges"
          @click="saveCurrentShoppingList">
          {{ tStatic('genericSave') }}
        </oie-base-action>
        <oie-base-action size="large" theme="skeleton" @click="handleCancel">
          {{ tStatic('cancel') }}
        </oie-base-action>
        <oie-base-action
          v-if="canDeleteList"
          size="large"
          theme="destructive"
          @click="handleDeleteShoppingList">
          <template #iconPrepend>
            <oie-base-icon icon="trash-can" size="s"></oie-base-icon>
          </template>
          {{ tStatic('deleteShoppingList') }}
        </oie-base-action>
      </template>
      <template #right>
        <oie-base-action
          v-element-loader="{ endpoint: 'addToCart' }"
          size="large"
          theme="secondary"
          @click="addCurrentShoppingListToCart">
          {{ tStatic('addListToCart') }}
        </oie-base-action>
      </template>
    </oie-action-wrapper>
  </oie-my-pages-template>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import myPages from '@/mixins/my-pages'
import { forEach } from 'lodash'

export default {
  mixins: [myPages],
  data: () => {
    return {
      uploadFileResponse: null
    }
  },
  computed: {
    ...mapGetters('myShoppingLists', ['currentShoppingList', 'selectedRows', 'currentShoppingListHasChanges', 'totalPriceCurrentShoppingList']),
    ...mapGetters('myMachines', ['hasMyMachines', 'machineOwnerDictionary', 'machineDictionary', 'globalMachine', 'globalMachineOwner', 'hasGlobalSelection', 'readableGlobalSelection']),
    machineMessage() {
      return this.hasGlobalSelection ? this.tStatic('addShoppingListToCartWithGlobalSelection').replace('{0}', this.readableGlobalSelection) : ''
    },
    canDeleteList() {
      const isOwnList = this.clientModel.isAuthenticated && (this.currentShoppingList.createdBy === this.clientModel.user.userName)
      const isAdmin = this.clientModel.isAuthenticated && this.clientModel.user.isUserAdmin
      return isOwnList || isAdmin
    }
  },
  watch: {
    currentShoppingListHasChanges(newVal) {
      if(newVal) {
        window.addEventListener('beforeunload', this.handleNavigation)
      } else {
        window.removeEventListener('beforeunload', this.handleNavigation)
      }
    }
  },
  mounted() {
    this.getSelectedShoppingList()

    this.subscribe('add-preview-row', ({ data }) => {
      this.addRow(data)
    })
  },
  methods: {
    ...mapActions('myShoppingLists',
      [
        'getSelectedShoppingList',
        'updateRow',
        'deleteRow',
        'addRow',
        'addRows',
        'saveCurrentShoppingList',
        'deleteShoppingList',
        'updateCurrentShoppingListProperty',
        'addCurrentShoppingListToCart',
        'setSelectedRows',
        'addSelectedRowsToCart',
        'deleteSelectedRows'
      ]),
    ...mapActions('ui', ['setActiveSlideIn']),

    async handleDeleteShoppingList() {
      this.publish('modal.show.generic', {
        header: this.tStatic('deleteShoppingListModalHeader'),
        text: `${this.tStatic('deleteShoppingListModalText')} "${this.currentShoppingList.name}"`,
        buttons: [ {
          text: this.tStatic('genericDelete'),
          usePrimaryButton: true,
          handler: async () => {
            await this.deleteShoppingList(this.currentShoppingList.id)
            this.publish('modal.hide.generic')
            this.goToSubPage(this.navigationOptions.oieMyShoppingLists)
          }
        }]
      })
    },
    handleUpdateMachineOrOwner({ row, machineOrOwner }) {
      const { machineId, machineOwnerId } = machineOrOwner
      this.updateRow({ row, value: machineId, property: 'machineId' })
      this.updateRow({ row, value: machineOwnerId, property: 'machineOwnerId' })
    },
    handleMarkSelectedRows({ machineId, machineOwnerId }) {
      forEach(this.selectedRows, row => {
        this.updateRow({ row, value: machineId, property: 'machineId' })
        this.updateRow({ row, value: machineOwnerId, property: 'machineOwnerId' })
      })
    },
    handleMarkListWithMachineOrOwner({ machineId, machineOwnerId }) {
      this.updateCurrentShoppingListProperty({ property: 'machineId', value: machineId })
      this.updateCurrentShoppingListProperty({ property: 'machineOwnerId', value: machineOwnerId })
    },
    handleCancel() {
      if(this.currentShoppingListHasChanges) {
        this.publish('modal.show.generic', {
          header: this.tStatic('cancelShoppingListChangesModalHeader'),
          text: this.tStatic('cancelShoppingListChangesModalText'),
          buttons: [ {
            text: this.tStatic('cancelShoppingListChangesModalButton'),
            usePrimaryButton: true,
            handler: () => {
              this.publish('modal.hide.generic')
              this.handleGoBackBehavior()
            }
          }]
        })
      } else {
        this.handleGoBackBehavior()
      }
    },
    handleGoBackBehavior() {
      if(this.navigationHistory.length >= 2) {
        this.goBack()
      } else {
        this.goToSubPage(this.navigationOptions.oieMyShoppingLists)
      }
    },
    handleNavigation(event) {
      event.preventDefault()
      event.returnValue = true
    }
  }
}
</script>

<style lang="scss">
.my-shopping-list__info {
  align-items: flex-start !important;
}

.my-shopping-list__machine-selection {
  margin-top: measure(half);
  display: flex;
  flex-direction: column;
  gap: measure(quarter);
  align-items: flex-end;
}

.my-shopping-list__machine-selection-actions {
  display: flex;
  gap: measure(eighth);
}

.my-shopping-list__machine-selection-info {
  display: flex;
  gap: measure(eighth);
  align-items: center;

}

.my-shopping-list__summary {
  display: flex;
  justify-content: flex-end;

  @include media('medium') {
    min-width: 20rem;
  }
}

.my-shopping-list__summary dl {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: measure(quarter);
}

</style>
